import React, { useState } from 'react';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Button,
    Divider,
    Alert,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VideocamIcon from '@mui/icons-material/Videocam';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeviceStatus from './DeviceStatus';
import DeviceControls from './DeviceControls';
import { useCancam } from '../../context/cancam/CancamProvider';

const DRAWER_WIDTH = 320;

const CancamDrawer: React.FC = () => {
    const {
        isConnected,
        connectedDevice,
        patrolPairingCode,
        patrolCodeExpiry,
        generatePatrolCode,
        pendingReplacement,
        confirmDeviceReplacement,
        deviceState,
        uploadProgress,
        isDrawerOpen,
        closeDrawer,
        startRecording,
        stopRecording,
        forceUpload,
        error,
        clearError,
    } = useCancam();

    const [showError, setShowError] = useState(false);

    // Show error snackbar when error changes
    React.useEffect(() => {
        if (error) {
            setShowError(true);
        }
    }, [error]);

    const handleCopyCode = () => {
        if (patrolPairingCode) {
            navigator.clipboard.writeText(patrolPairingCode);
        }
    };

    const handleCloseError = () => {
        setShowError(false);
        clearError();
    };

    return (
        <>
            <Drawer
                anchor="right"
                open={isDrawerOpen}
                onClose={closeDrawer}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: DRAWER_WIDTH,
                        boxSizing: 'border-box',
                    },
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VideocamIcon color="primary" />
                        <Typography variant="h6">CANcam</Typography>
                    </Box>
                    <IconButton onClick={closeDrawer} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Content */}
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Device Status */}
                    <DeviceStatus
                        isConnected={isConnected}
                        connectedDevice={connectedDevice}
                        deviceState={deviceState}
                    />

                    <Divider />

                    {/* Pairing/Controls Section */}
                    {!connectedDevice ? (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            {patrolPairingCode ? (
                                // Show generated code
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Enter this code on the camera:
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 1,
                                            my: 2,
                                        }}
                                    >
                                        <Typography
                                            variant="h3"
                                            sx={{
                                                fontFamily: 'monospace',
                                                letterSpacing: '0.3em',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            {patrolPairingCode}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={handleCopyCode}
                                            title="Copy code"
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Expires in {patrolCodeExpiry}s
                                    </Typography>
                                    <Box sx={{ mt: 2 }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={generatePatrolCode}
                                        >
                                            Generate New Code
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                // Show generate button
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Connect a camera to record this patrol
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<QrCode2Icon />}
                                        onClick={generatePatrolCode}
                                        sx={{ mt: 2 }}
                                    >
                                        Generate Pairing Code
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        // Device connected - show controls
                        <DeviceControls
                            connectedDevice={connectedDevice}
                            deviceState={deviceState}
                            uploadProgress={uploadProgress}
                            onStartRecording={startRecording}
                            onStopRecording={stopRecording}
                            onForceUpload={forceUpload}
                        />
                    )}
                </Box>
            </Drawer>

            {/* Device Replacement Dialog */}
            <Dialog
                open={pendingReplacement !== null}
                onClose={() => confirmDeviceReplacement(false)}
            >
                <DialogTitle>Replace Device?</DialogTitle>
                <DialogContent>
                    <Typography>
                        A different camera is trying to connect to this patrol.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Current: {pendingReplacement?.existingDeviceName || 'Unknown device'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        New: {pendingReplacement?.newDeviceName || 'Unknown device'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => confirmDeviceReplacement(false)}>
                        Keep Current
                    </Button>
                    <Button
                        onClick={() => confirmDeviceReplacement(true)}
                        variant="contained"
                        color="warning"
                    >
                        Replace
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Error Snackbar */}
            <Snackbar
                open={showError && !!error}
                autoHideDuration={6000}
                onClose={handleCloseError}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </>
    );
};

export default CancamDrawer;
